import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { getQuestionDescription, updateQuestionDescription } from "../../api/questionsApi";
import { NavigationBadge, useNavigationBadgesStore } from "../..";
import { useEffect, useState } from "react";
import ItemsList from "../../components/lists/ItemsList";
import { BaseDto } from "../../types/Rows";

export const QuestionPage = () => {
  let { questionId } = useParams();
  const setItems = useNavigationBadgesStore((state: any) => state.setItems);
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editedDescription, setEditedDescription] = useState("");

  const { data, isFetching, error, refetch } = useQuery(
    ["Question", questionId!],
    () => getQuestionDescription(questionId!),
    {}
  );

  const updateDescriptionMutation = useMutation({
    mutationFn: (newDescription: string) => 
      updateQuestionDescription(questionId!, newDescription),
    onSuccess: () => {
      queryClient.invalidateQueries(["Question", questionId!]);
      setIsEditing(false);
    },
  });

  useEffect(() => {
    if (data) {
      setEditedDescription(data.description);
      setItems([
        { text: "Pytania", url: "/Questions" } as NavigationBadge,
        {
          text: data.description.substring(0, 40) + "...",
          url: "Questions/" + data.id,
        } as NavigationBadge,
      ]);
    }
  }, [data, setItems]);

  const handleSaveDescription = () => {
    updateDescriptionMutation.mutate(editedDescription);
  };

  const handleCancelEdit = () => {
    setEditedDescription(data?.description || "");
    setIsEditing(false);
  };

  if (isFetching || !data) return <>Loading</>;

  return (
    <div className="App">
      <div className="p-5 flex flex-col gap-5">
        <div>
          <div className="flex content-between justify-between">
            <div className="flex-1">
              {isEditing ? (
                <div className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    className="text-lg font-semibold text-[var(--sc-text)] bg-transparent border-b border-[var(--sc-border)] focus:border-[var(--sc-amber)] outline-none flex-1"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveDescription}
                    disabled={updateDescriptionMutation.isLoading}
                    className="px-3 py-1 bg-[#F4A832] text-[#0F0F0F] rounded-full text-sm hover:bg-[#e09620] disabled:opacity-50"
                  >
                    {updateDescriptionMutation.isLoading ? "Saving..." : "Save"}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-1 bg-[var(--sc-bg)] text-[var(--sc-muted)] border border-[var(--sc-border)] rounded-full text-sm hover:bg-[var(--sc-border)]"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h1 className="text-3xl font-extrabold dark:text-white">
                    {data.description}
                  </h1>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="text-[#F4A832] hover:text-[#e09620] text-sm"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <ItemsList
          isLoading={isFetching}
          data={data.questions.flatMap((reportQuestion) =>
            reportQuestion.answers.map(
              (x) =>
                ({
                  name:
                    x.resortName +
                    ", " +
                    x.reportName +
                    " - " +
                    (x.answer === "yes" ? "tak" : "nie"),
                  id: x.reportId,
                  created: x.created,
                  createdBy: x.createdBy,
                  description: x.comment,
                } as BaseDto)
            )
          )}
          urlPrefix="/reports/"
          isSortVisible
        >
          <header>
            <div className="max-w-7xl">
              <h1 className="text-[15px] font-semibold text-[var(--sc-text)]">
                Odopowiedzi
              </h1>
            </div>
          </header>{" "}
        </ItemsList>
      </div>
    </div>
  );
};
